import styled, { css } from "styled-components";
import Link from "next/link";
import {
  BodyRegularCSS,
  XSmallBodyBoldCSS,
  XSmallBodyMediumCSS,
  XSmallBodyRegularCSS,
} from "@styles/typography";
import { Color, Ease, Device } from "@styles/constants";
import { AbsoluteCoverCSS } from "@styles/layout";
import { ButtonStyles } from "@components/ui/button";

import iconSelect from "@images/caret_black.svg";

export const Back = styled.div`
  margin: 30px 0 50px;

  @media ${Device.mediumUp} {
    margin: 65px 0 52px;
  }
`;

export const BackButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  padding: 7px 14px;
  text-decoration: none;
  border-radius: 48px;
  border: 1px solid ${Color.blackBrand};
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.15);
  transition: background ${Ease.duration} ${Ease.out}, color ${Ease.duration} ${Ease.out};

  svg {
    display: inline-block;
    width: 17px;
    height: 17px;
    margin-right: 5px;
    fill: ${Color.blackBrand};
    transition: fill ${Ease.duration} ${Ease.out};
  }

  &:focus-visible,
  &:hover {
    background: ${Color.blackBrand};
    color: ${Color.white};

    svg {
      fill: ${Color.white};
    }
  }
`;

export const ModalTrigger = styled.div`
  position: relative;
  border-bottom: 1px solid ${Color.blackBrand};

  button {
    ${XSmallBodyBoldCSS}
    cursor: pointer;
    margin-left: 3px;
    display: block;
    padding: 15px 0;
    text-decoration: none;
    text-align: left;
    width: 100%;

    &:focus-visible {
      outline: -webkit-focus-ring-color auto 1px;
    }
  }

  svg {
    position: absolute;
    top: 50%;
    right: 0;
    transform: translateY(-50%);
    pointer-events: none;
  }

  .affirm-as-low-as {
    ${AbsoluteCoverCSS}
    display: block;
    text-indent: -9999px;
  }

  .affirm-ala-price {
    display: none;
  }

  .affirm-modal-trigger {
    ${AbsoluteCoverCSS}
    z-index: 1;
    text-indent: -9999px;
  }
`;

export const BundlePromotion = styled.div`
  position: relative;
  background: ${Color.blueAsh};
  border-radius: 4px;
  padding: 10px 20px;
  margin: 26px 0;

  p {
    ${XSmallBodyRegularCSS}
    margin-bottom: 0;
    text-align: center;

    span {
      ${XSmallBodyBoldCSS}
    }
  }

  svg {
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 20px;
  }
`;

export const Container = styled.div<{ bundle: boolean }>`
  margin-bottom: 100px;
  ${({ bundle }) =>
    bundle &&
    css`
      @media ${Device.largeUp} {
        padding-bottom: 175px;
        border-bottom: 1px solid ${Color.goldAsh};
      }
    `}
`;

export const Details = styled.div`
  margin-top: 30px;
  height: 100%;
  display: flex;
  flex-direction: column;

  @media ${Device.largeUp} {
    margin-top: 0;
    padding: 0 45px;
  }

  a {
    &:focus-visible {
      outline: -webkit-focus-ring-color auto 1px;
    }
  }
`;

export const Footer = styled.footer`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  > button {
    ${ButtonStyles}
    display: flex !important;
    justify-content: center;
    width: 100%;
    align-items: center;
    text-transform: none;
    margin-top: 26px;

    &:focus-visible {
      outline: -webkit-focus-ring-color auto 1px;
    }
  }

  .pricing {
    .price-wrapper {
      padding-top: 0;

      h3,
      s {
        ${BodyRegularCSS}
        margin-bottom: 0;
      }

      s {
        margin-left: 10px;
      }
    }
  }
`;

export const FreeTrialWrapper = styled.div`
  ${XSmallBodyMediumCSS}
  color: ${Color.black60};
  padding: 20px 10px 0;
  margin: 0 auto;
  text-align: center;
  max-width: 44ch;

  @media ${Device.mediumUp} {
    padding-top: 30px;
  }

  @media ${Device.mediumDown} {
    width: 90%;
  }

  .black-bold {
    font-weight: normal;
  }

  strong {
    font-family: $font-sans-serif-semibold;
    color: ${Color.black};
  }

  button {
    color: ${Color.black};
    text-decoration: underline;
  }

  u {
    color: ${Color.black};
  }
`;

export const FreeTrial = styled.div`
  p {
    display: inline;
  }

  button {
    margin-left: 4px;
  }
`;

export const Footnote = styled.p`
  padding-top: 20px;
  display: block;

  @media ${Device.mediumUp} {
    padding-top: 30px;
  }
`;

export const Heading = styled.div`
  display: none;
  margin-bottom: 30px;

  h1 {
    text-align: left;
    margin-bottom: 10px;
    margin-top: -17px;
  }

  p {
    max-width: 50ch;
    margin-bottom: 0;
  }

  @media ${Device.largeUp} {
    display: block;
  }
`;

export const Meta = styled.div`
  padding: 15px 0;
  border-top: 2px solid ${Color.blackBrand};
  border-bottom: 1px solid ${Color.blackBrand};

  span {
    display: block;
    ${XSmallBodyBoldCSS}
  }

  p {
    ${XSmallBodyRegularCSS}
    margin-top: 15px;
    margin-bottom: 0;
    max-width: 60ch;
  }
`;

export const MobileHeading = styled(Heading)`
  display: block;

  @media ${Device.largeUp} {
    display: none;
  }
`;

export const ProductDescription = styled.div`
  margin-top: 0 !important;
`;

// Variant Select
export const SelectRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  column-gap: 10px;

  div[type="select"] {
    &::after {
      display: none;
    }
  }

  .variant-select {
    flex: 0 0 calc(50% - 5px);
    width: calc(50% - 5px);

    select {
      height: auto;
      padding: 10px;
      background: url(${iconSelect}) right 10px center/8px no-repeat;
      border: 1px solid #646567;
      ${XSmallBodyMediumCSS}
    }

    label {
      ${XSmallBodyMediumCSS}
      top: 11px;
      left: 10px;
    }
  }

  .variant-select-description {
    ${XSmallBodyRegularCSS}
    color: ${Color.grayBrand};
    margin-top: -1.2em;
    padding-left: 8px;
  }

  p {
    flex: 0 0 100%;
  }
`;

export const SubItemSelectBlock = styled.div`
  width: 100%;
  height: 100%;
  margin-top: 15px;
`;

export const SubItemSelectHeader = styled.div`
  position: relative;
  padding-left: 10px;

  h4 {
    ${XSmallBodyBoldCSS}
    text-align: left;
    margin-bottom: 0;
  }

  p {
    ${XSmallBodyRegularCSS}
    color: ${Color.grayBrand}
  }

  &::before {
    content: "*";
    color: red;
    position: absolute;
    top: 0;
    left: 0;
    line-height: 1;
  }
`;


export const AffirmCallout = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 20px;
`